// src/components/layout/Navbar.jsx
//
// The top bar from the design: a search pill at (252, 22), the bell at (1150, 22) and
// the user block at (1214, 20), all sitting directly on the backdrop.
//
// `compact` (tablets / phones, see MainLayout): a sticky full-width bar instead — menu
// button, logo, a search button that opens the search field, the bell and the avatar.
import React, { useEffect, useState } from 'react';
import { LogOut, User, Sun, Moon, Menu, Search, X } from 'lucide-react';
import BrandLogo from 'assets/brand-logo.png';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { logout } from '../../store/slices/authSlice';
import {
  fetchNotifications,
  fetchUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
  clearAllNotifications,
  clearError,
} from '../../store/slices/notificationSlice';
import { fetchMyProfile } from '../../store/slices/profileSlice';
import useNotificationSocket from '../../hooks/useNotificationSocket';
import { cn } from '../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';
import { useTheme } from 'next-themes';
import { format, isValid } from 'date-fns';
import { getNotificationPath } from 'utlis/notificationRoutes';
import { getDeviceId } from 'utlis/helperFunction';
import authApi from '../../api/authApi';

const INK = 'rgb(43, 20, 24)';
const INK_SOFT = 'rgb(90, 58, 63)';

const Navbar = ({ compact = false, onMenu }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();

  const { user: authUser } = useSelector((state) => state.auth);
  const { profile, isLoading: profileLoading } = useSelector((s) => s.profile);
  const { list: notifications, unreadCount, isLoading: notifLoading, error: notifError } =
    useSelector((s) => s.notification);

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  useNotificationSocket();

  useEffect(() => {
    dispatch(fetchNotifications({ page: 1, pageSize: 10 }));
    dispatch(fetchUnreadCount());
  }, [dispatch]);

  useEffect(() => {
    if (authUser?.id) dispatch(fetchMyProfile());
  }, [dispatch, authUser?.id]);

  useEffect(() => {
    if (notifError) {
      toast.error(notifError);
      dispatch(clearError());
    }
  }, [notifError, dispatch]);

  const handleNotificationClick = async (notification) => {
    if (!notification.read) await dispatch(markNotificationRead(notification.id));
    setPopoverOpen(false);
    navigate(getNotificationPath(notification));
  };

  const handleLogout = async () => {
    try {
      await authApi.logout({ device_id: getDeviceId() });
    } catch (error) {
      console.error('Logout API failed:', error);
    } finally {
      setTheme('light');
      dispatch(logout());
      navigate('/login', { replace: true });
      setTimeout(() => window.location.reload(), 100);
    }
  };

  const submitSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    if (q) navigate(`/work-task-assignment?search=${encodeURIComponent(q)}`);
    setSearchOpen(false);
  };

  const displayName = profile?.full_name || 'User';
  const displayRole = profile?.role_name || 'Employee';
  const profileImage = profile?.profile_img;
  const initials = `${(profile?.first_name || 'U').charAt(0)}${(profile?.last_name || 'S').charAt(0)}`.toUpperCase();

  const roundBtn = {
    width: 40, height: 40, borderRadius: '50%', background: 'rgba(255, 255, 255, 0.85)',
    border: '1px solid rgba(255, 255, 255, 0.95)', display: 'flex', alignItems: 'center',
    justifyContent: 'center', cursor: 'pointer', flexShrink: 0, color: '#7a1f2b',
  };
  // compact: items flow in a flex row; desktop: each sits at its design position
  const place = (desktop, mobile = {}) => (compact ? { position: 'relative', ...mobile } : desktop);

  return (
    <div style={compact ? {
      position: 'sticky', top: 0, zIndex: 30, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
      padding: '10px 12px', background: 'hsl(var(--app-backdrop) / 0.96)', borderBottom: '1px solid rgba(94, 20, 30, 0.08)',
    } : { position: 'absolute', left: 0, top: 0, width: 1536, height: 72, zIndex: 30, pointerEvents: 'none' }}>
      {compact && (
        <>
          <button type="button" onClick={onMenu} aria-label="Open menu" style={roundBtn}><Menu size={20} /></button>
          <Link to="/dashboard" aria-label="Dashboard" style={{ display: 'flex', alignItems: 'center', marginRight: 'auto' }}>
            <img src={BrandLogo} alt="Mohilya" style={{ height: 34, width: 'auto', display: 'block' }} />
          </Link>
          <button type="button" onClick={() => setSearchOpen((v) => !v)} aria-label={searchOpen ? 'Close search' : 'Search'} style={roundBtn}>
            {searchOpen ? <X size={18} /> : <Search size={18} />}
          </button>
        </>
      )}
      {/* Search pill */}
      {(!compact || searchOpen) && (
      <form
        onSubmit={submitSearch}
        role="search"
        style={{
          pointerEvents: 'auto', ...place({ position: 'absolute', left: 252, top: 22, width: 454 }, { order: 10, width: '100%' }), height: 42,
          borderRadius: 12, background: 'rgba(255, 255, 255, 0.9)', border: '1px solid rgba(255, 255, 255, 0.95)',
          display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px', color: 'rgb(125, 106, 108)', fontSize: 14,
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5a3a3f" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
        </svg>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search for tasks, orders, designs, fabrics…"
          aria-label="Search"
          autoFocus={compact}
          className="placeholder:text-[rgb(125,106,108)]"
          style={{ flex: 1, minWidth: 0, background: 'transparent', border: 0, outline: 'none', fontSize: 14, color: INK }}
        />
      </form>
      )}

      {/* Notifications */}
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`Notifications${unreadCount ? ` (${unreadCount} unread)` : ''}`}
            style={{
              pointerEvents: 'auto', ...place({ position: 'absolute', left: 1150, top: 22 }), width: 40, height: 40,
              borderRadius: '50%', background: 'rgba(255, 255, 255, 0.85)', border: '1px solid rgba(255, 255, 255, 0.95)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7a1f2b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
            </svg>
            {unreadCount > 0 && (
              <span style={{ position: 'absolute', top: 7, right: 9, width: 8, height: 8, borderRadius: '50%', background: 'rgb(229, 73, 90)' }} />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80 max-w-[calc(100vw-24px)] p-0 mr-3" align="end">
          <div className="border-b p-4 flex justify-between items-center">
            <div>
              <h4 className="font-semibold">Notifications</h4>
              <p className="text-sm text-muted-foreground">{unreadCount} unread</p>
            </div>
            <div className="flex gap-2">
              {unreadCount > 0 && (
                <Button variant="ghost" size="sm" onClick={async () => { await dispatch(markAllNotificationsRead()); toast.success('All notifications marked as read'); }}>
                  Mark all read
                </Button>
              )}
              {notifications.length > 0 && (
                <Button variant="ghost" size="sm" onClick={async () => { await dispatch(clearAllNotifications()); toast.success('All notifications cleared'); }}>
                  Clear all
                </Button>
              )}
            </div>
          </div>
          <ScrollArea className="h-80">
            {notifLoading && !notifications.length ? (
              <div className="p-4 text-center text-sm text-muted-foreground">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">No notifications</div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n?.id}
                  className={cn('p-3 border-b hover:bg-muted/50 cursor-pointer transition-colors', !n?.read && 'bg-accent/5')}
                  onClick={() => handleNotificationClick(n)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="text-sm font-medium">{n?.message}</p>
                      <p className="text-xs text-muted-foreground">
                        {n?.created_at
                          ? ((date) => (isValid(date) ? format(date, 'MMM d, yyyy h:mm a') : 'Invalid Date'))(
                              n.created_at?.toDate?.() || new Date(n.created_at))
                          : '—'}
                      </p>
                      {n?.type_display && <Badge variant="secondary" className="mt-1 text-xs">{n?.type_display}</Badge>}
                    </div>
                    {!n?.read && <div className="w-2 h-2 rounded-full bg-primary mt-1" />}
                  </div>
                </div>
              ))
            )}
          </ScrollArea>
          <div className="p-2 border-t">
            <Button variant="ghost" className="w-full text-xs" size="sm" onClick={() => { setPopoverOpen(false); navigate('/notifications'); }}>
              View all
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {/* User block */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Account menu"
            style={{
              pointerEvents: 'auto', ...place({ position: 'absolute', left: 1214, top: 20 }), display: 'flex', alignItems: 'center',
              gap: 14, background: 'transparent', border: 0, padding: 0, cursor: 'pointer', color: INK, textAlign: 'left', flexShrink: 0,
            }}
          >
            <span
              style={{
                width: compact ? 40 : 46, height: compact ? 40 : 46, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
                background: 'linear-gradient(135deg, rgb(122, 31, 43), rgb(200, 54, 43))', border: '2px solid rgb(255, 255, 255)',
                color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15,
              }}
            >
              {profileImage && !profileLoading
                ? <img src={profileImage} alt="" crossOrigin="anonymous" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : initials}
            </span>
            {!compact && (
            <>
            <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.35 }}>
              <span style={{ fontSize: 16, fontWeight: 700 }}>{displayName}</span>
              <span style={{ fontSize: 13, color: INK_SOFT }}>{displayRole}</span>
            </span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5a3a3f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 34 }} aria-hidden="true">
              <path d="m6 9 6 6 6-6" />
            </svg>
            </>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56" align="end">
          <DropdownMenuLabel>
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium">{displayName}</p>
              <p className="text-xs text-muted-foreground">{profile?.email}</p>
              <DropdownMenuSeparator />
              <span className="text-xs text-muted-foreground">{displayRole}</span>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuItem asChild>
            <Link to="/profile" className="cursor-pointer flex items-center">
              <User className="mr-2 h-4 w-4" /> Profile
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} className="cursor-pointer">
            {theme === 'dark' ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
            {theme === 'dark' ? 'Light mode' : 'Dark mode'}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive">
            <LogOut className="mr-2 h-4 w-4" /> Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default Navbar;
